import React from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "./NavBar";

export default function RefundPolicyPage({ user, onLogout }) {
  const navigate = useNavigate();
  const s = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "transparent", color: "#0f172a", minHeight: "100vh" },
    main: { maxWidth: 800, margin: "0 auto", padding: "32px 20px" },
    backBtn: { background: "transparent", border: "none", color: "#64748b", cursor: "pointer", fontSize: 14, marginBottom: 24, padding: 0 },
    hero: { background: "rgba(255,255,255,0.75)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 20, padding: "32px 36px", marginBottom: 32, backdropFilter: "blur(8px)", boxShadow: "0 2px 12px rgba(59,130,246,0.08)" },
    h1: { fontSize: 32, fontWeight: 800, color: "#0f172a", marginBottom: 8 },
    meta: { fontSize: 14, color: "#64748b" },
    section: { background: "rgba(255,255,255,0.75)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 16, padding: "24px 28px", marginBottom: 16, backdropFilter: "blur(8px)", boxShadow: "0 2px 8px rgba(59,130,246,0.06)" },
    h2: { fontSize: 18, fontWeight: 700, color: "#1e293b", marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid rgba(59,130,246,0.08)" },
    h3: { fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 8, marginTop: 16 },
    p: { fontSize: 14, color: "#475569", lineHeight: 1.8, marginBottom: 12 },
    ul: { fontSize: 14, color: "#475569", lineHeight: 1.8, paddingLeft: 20, marginBottom: 12 },
    li: { marginBottom: 6 },
    greenBox: { background: "rgba(22,163,74,0.06)", border: "1px solid rgba(22,163,74,0.2)", borderRadius: 10, padding: "12px 16px", fontSize: 13, color: "#15803d", marginBottom: 12 },
    redBox: { background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.15)", borderRadius: 10, padding: "12px 16px", fontSize: 13, color: "#dc2626", marginBottom: 12 },
    contact: { background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.15)", borderRadius: 12, padding: "16px 20px", marginTop: 8 },
  };

  return (
    <div style={s.root}>
      {user ? <NavBar user={user} onLogout={onLogout} /> : (
        <nav style={{ padding: "16px 40px", borderBottom: "1px solid rgba(59,130,246,0.15)", background: "rgba(238,242,251,0.95)", backdropFilter: "blur(12px)" }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", cursor: "pointer" }} onClick={() => navigate("/")}>
            i<span style={{ color: "#3b82f6" }}>Trust Secure</span>
          </div>
        </nav>
      )}
      <div style={s.main}>
        <button style={s.backBtn} onClick={() => navigate(-1)}>← Back</button>
        <div style={s.hero}>
          <div style={s.h1}>Refund & Dispute Policy</div>
          <div style={s.meta}>iTrust Secure LLC · itrustsecure.com · Effective Date: May 12, 2026 · Last Updated: May 12, 2026</div>
        </div>

        <div style={s.section}>
          <div style={s.h2}>1. Overview</div>
          <p style={s.p}>iTrust Secure operates as an escrow marketplace. Buyer payments are held securely until delivery is confirmed. This policy explains how refunds are issued, how disputes are handled, and what protections are available to both buyers and sellers.</p>
        </div>

        <div style={s.section}>
          <div style={s.h2}>2. Escrow Process</div>
          <p style={s.p}>When a buyer funds an order on iTrust Secure:</p>
          <ul style={s.ul}>
            <li style={s.li}>Payment is held in escrow and is not accessible to the seller</li>
            <li style={s.li}>The seller delivers the goods or services and submits proof of delivery</li>
            <li style={s.li}>The buyer reviews the delivery and either confirms receipt or opens a dispute</li>
            <li style={s.li}>Upon confirmation, funds are released to the seller</li>
            <li style={s.li}>iTrust charges a platform fee of 2.5% (or 2% for Premium transactions) deducted at the time of release</li>
          </ul>
        </div>

        <div style={s.section}>
          <div style={s.h2}>3. Refund Eligibility</div>
          <div style={s.greenBox}>✅ Refunds ARE available in these circumstances:</div>
          <ul style={s.ul}>
            <li style={s.li}>The buyer opens a dispute and iTrust resolves it in the buyer's favor</li>
            <li style={s.li}>The seller fails to deliver within the agreed timeframe</li>
            <li style={s.li}>The item received materially differs from the description provided</li>
            <li style={s.li}>iTrust determines fraud or misrepresentation occurred on the part of the seller</li>
            <li style={s.li}>An admin override refund is issued by iTrust due to a platform error</li>
          </ul>
          <div style={s.redBox}>❌ Refunds are NOT available in these circumstances:</div>
          <ul style={s.ul}>
            <li style={s.li}>The buyer has already confirmed delivery</li>
            <li style={s.li}>The payment has already been released to the seller</li>
            <li style={s.li}>The buyer changes their mind after delivery is confirmed</li>
            <li style={s.li}>The dispute is resolved in the seller's favor</li>
            <li style={s.li}>The buyer fails to open a dispute within the dispute window</li>
          </ul>
        </div>

        <div style={s.section}>
          <div style={s.h2}>4. Auto-Release Policy</div>
          <p style={s.p}>If a buyer does not confirm delivery and does not open a dispute within 72 hours of the most recent delivery proof submission, the payment will be automatically released to the seller.</p>
          <p style={s.p}>Auto-release will NOT occur if:</p>
          <ul style={s.ul}>
            <li style={s.li}>An active dispute is open on the order</li>
            <li style={s.li}>The order has been refunded or cancelled</li>
            <li style={s.li}>iTrust has placed a manual hold on the transaction</li>
          </ul>
        </div>

        <div style={s.section}>
          <div style={s.h2}>5. Dispute Process</div>
          <div style={s.h3}>5.1 Opening a Dispute</div>
          <p style={s.p}>Either the buyer or seller may open a dispute on a funded order. To open a dispute, navigate to your order and select "File a Dispute." You must provide a reason and photo evidence. Once opened, the payment is frozen until the dispute is resolved.</p>
          <div style={s.h3}>5.2 Dispute Review</div>
          <p style={s.p}>Once a dispute is opened, iTrust administrators will review the reason, request additional evidence if necessary, review delivery proofs and communications, and move the dispute to UNDER REVIEW status while investigating.</p>
          <div style={s.h3}>5.3 Dispute Resolution</div>
          <p style={s.p}>Possible outcomes are:</p>
          <ul style={s.ul}>
            <li style={s.li}><strong>Buyer wins</strong> — Payment is refunded to the buyer</li>
            <li style={s.li}><strong>Seller wins</strong> — Payment is released to the seller and order is marked complete</li>
          </ul>
          <p style={s.p}>iTrust's resolution decision is final. We aim to resolve all disputes within 5-7 business days.</p>
        </div>

        <div style={s.section}>
          <div style={s.h2}>6. Transaction Tiers and Protections</div>
          <ul style={s.ul}>
            <li style={s.li}><strong>Standard ($1 – $500)</strong> — Standard escrow protection and dispute resolution</li>
            <li style={s.li}><strong>Plus ($501 – $5,000)</strong> — Priority dispute resolution with faster review times</li>
            <li style={s.li}><strong>Premium ($5,001+)</strong> — Dedicated admin review, extended dispute window, and enhanced fraud protection</li>
          </ul>
        </div>

        <div style={s.section}>
          <div style={s.h2}>7. Fraud Prevention</div>
          <p style={s.p}>Actions that may result in immediate account suspension and forfeiture of funds include:</p>
          <ul style={s.ul}>
            <li style={s.li}>Submitting false delivery proofs</li>
            <li style={s.li}>Opening fraudulent disputes to delay payment</li>
            <li style={s.li}>Misrepresenting items or services</li>
            <li style={s.li}>Using stolen payment methods</li>
            <li style={s.li}>Colluding with other users to manipulate the escrow system</li>
          </ul>
        </div>

        <div style={s.section}>
          <div style={s.h2}>8. Contact</div>
          <div style={s.contact}>
            <p style={{ ...s.p, marginBottom: 4 }}><strong>iTrust Secure LLC</strong></p>
            <p style={{ ...s.p, marginBottom: 4 }}>Email: founder@itrustsecure.com</p>
            <p style={{ ...s.p, marginBottom: 4 }}>Website: itrustsecure.com</p>
            <p style={{ ...s.p, marginBottom: 0 }}>Authorized Representative: Samuel NanaYaw Afunyah Blankson</p>
          </div>
        </div>
      </div>
    </div>
  );
}